class VariantGallery {
  constructor() {
    this.productSection = document.querySelector('[data-section], .product-main-section, #MainProduct, .product');
    this.galleryViewer = document.querySelector('product-gallery');
    this.thumbnailSlider = document.querySelector('thumbnail-slider');
    this.isProcessing = false;
    this.currentVariantId = null;
    
    if (this.productSection) {
      this.init();
    }
  }

  init() {
    this.setupEventListeners();
    this.checkInitialVariant();
  }

  setupEventListeners() {
    // Primary variant change listener
    document.addEventListener('variant:change', (event) => {
      if (event.detail?.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });

    // Fallback listeners for direct form changes
    const variantInputs = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        if (input.value) {
          this.handleVariantChange({ id: input.value });
        }
      });
    });

    // Option change listener with debouncing
    const optionInputs = document.querySelectorAll('input[name^="option"], select[name^="option"]');
    if (optionInputs.length > 0) {
      const debouncedHandler = this.debounce(() => {
        const variant = this.getCurrentVariant();
        if (variant) this.handleVariantChange(variant);
      }, 100);
      
      optionInputs.forEach(input => {
        input.addEventListener('change', debouncedHandler);
      });
    }
  }

  checkInitialVariant() {
    // Check URL parameter
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) {
      setTimeout(() => this.handleVariantChange({ id: urlVariant }), 100);
      return;
    }

    // Check for existing variant gallery
    setTimeout(() => {
      if (document.querySelector('.variant-gallery-item')) {
        this.ensureFirstImageActive();
      }
    }, 100);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  getCurrentVariant() {
    // Try form input first
    const variantInput = document.querySelector('product-form input[name="id"]');
    if (variantInput?.value) {
      return { id: variantInput.value };
    }
    
    // Fallback to URL parameter
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    return urlVariant ? { id: urlVariant } : null;
  }

  async handleVariantChange(variant) {
    if (!variant?.id || this.isProcessing || this.currentVariantId === variant.id) {
      return;
    }

    this.isProcessing = true;
    this.currentVariantId = variant.id;

    try {
      const hasVariantGallery = await this.checkVariantGallery(variant.id);
      
      if (hasVariantGallery) {
        await this.loadVariantGallery(variant.id);
      } else {
        this.restoreOriginalGallery();
      }
    } catch {
      this.restoreOriginalGallery();
    } finally {
      this.isProcessing = false;
    }
  }


  async checkVariantGallery(variantId) {
    try {
      const sectionId = document.querySelector('[data-section]')?.dataset.section || 'main-product';
      
      const response = await fetch(`${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`, {
        headers: {
          'Accept': 'text/html',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelectorAll('.variant-gallery-item').length > 0;
      }
    } catch {
      // Silent error handling
    }
    return false;
  }

  async loadVariantGallery(variantId) {
    try {
      const sectionId = this.productSection.dataset.section;
      const response = await fetch(`${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`);
      
      if (!response.ok) throw new Error('Failed to fetch');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const galleryItems = doc.querySelectorAll('.variant-gallery-item');
      const thumbnailItems = doc.querySelectorAll('.variant-gallery-thumbnail');

      if (galleryItems.length > 0) {
        this.updateGallery(galleryItems, thumbnailItems);
      } else {
        this.restoreOriginalGallery();
      }
    } catch {
      this.restoreOriginalGallery();
    }
  }

  updateGallery(galleryItems, thumbnailItems) {
    this.updateMainGallery(galleryItems);
    this.updateThumbnails(thumbnailItems);
    this.resetGalleryState();
    this.enableAddToCartButton();
  }

  updateMainGallery(galleryItems) {
    const galleryList = document.querySelector('.product__media-list');
    if (!galleryList) return;

    // Hide existing items and remove variant items
    galleryList.querySelectorAll('.product__media-item').forEach(item => {
      item.style.display = 'none';
      item.classList.remove('is-active');
    });
    galleryList.querySelectorAll('.variant-gallery-item').forEach(item => item.remove());

    // Add new variant gallery items
    galleryItems.forEach((item, index) => {
      const clonedItem = item.cloneNode(true);
      clonedItem.classList.toggle('is-active', index === 0);
      galleryList.appendChild(clonedItem);
    });
  }

  updateThumbnails(thumbnailItems) {
    const thumbnailList = document.querySelector('.thumbnail-list');
    if (!thumbnailList || !thumbnailItems.length) return;

    // Hide existing thumbnails and remove variant thumbnails
    thumbnailList.querySelectorAll('.thumbnail-list__item').forEach(item => {
      item.style.display = 'none';
      item.classList.remove('is-active');
    });
    thumbnailList.querySelectorAll('.variant-gallery-thumbnail').forEach(item => item.remove());

    // Add new variant thumbnails
    thumbnailItems.forEach((item, index) => {
      const clonedItem = item.cloneNode(true);
      clonedItem.classList.toggle('is-active', index === 0);
      thumbnailList.appendChild(clonedItem);
    });
  }

  restoreOriginalGallery() {
    const galleryList = document.querySelector('.product__media-list');
    const thumbnailList = document.querySelector('.thumbnail-list');

    if (galleryList) {
      // Remove variant items and restore originals
      galleryList.querySelectorAll('.variant-gallery-item').forEach(item => item.remove());
      galleryList.querySelectorAll('.product__media-item:not(.variant-gallery-item)').forEach((item, index) => {
        item.style.display = '';
        item.classList.toggle('is-active', index === 0);
      });
    }

    if (thumbnailList) {
      // Remove variant thumbnails and restore originals
      thumbnailList.querySelectorAll('.variant-gallery-thumbnail').forEach(item => item.remove());
      thumbnailList.querySelectorAll('.thumbnail-list__item:not(.variant-gallery-thumbnail)').forEach((item, index) => {
        item.style.display = '';
        item.classList.toggle('is-active', index === 0);
      });
    }

    this.resetGalleryState();
  }

  resetGalleryState() {
    // Trigger gallery refresh
    this.galleryViewer?.dispatchEvent(new CustomEvent('gallery:refresh'));
    
    // Reinitialize thumbnail handlers
    this.reinitializeThumbnailHandlers();
    
    // Update media count
    this.updateMediaCount();
  }

  updateMediaCount() {
    const galleryList = document.querySelector('.product__media-list');
    const thumbnailList = document.querySelector('.thumbnail-list');
    
    if (!galleryList) return;
    
    let mediaCount = 0;
    const variantItems = galleryList.querySelectorAll('.variant-gallery-item');
    
    if (variantItems.length > 0) {
      mediaCount = variantItems.length;
    } else {
      // Count visible gallery items
      const visibleGalleryItems = Array.from(galleryList.querySelectorAll('.product__media-item'))
        .filter(item => !item.classList.contains('variant-gallery-item') && 
                       window.getComputedStyle(item).display !== 'none').length;
      
      // Count visible non-variant thumbnails
      const visibleThumbnails = thumbnailList ? 
        Array.from(thumbnailList.querySelectorAll('.thumbnail-list__item'))
          .filter(thumb => !thumb.classList.contains('thumbnail-list__item--variant') &&
                          !thumb.classList.contains('variant-gallery-thumbnail') &&
                          window.getComputedStyle(thumb).display !== 'none').length : 0;
      
      mediaCount = Math.max(visibleGalleryItems, visibleThumbnails);
    }
    
    // Update data attributes
    if (thumbnailList) thumbnailList.dataset.mediaCount = mediaCount;
    galleryList.dataset.mediaCount = mediaCount;
  }

  reinitializeThumbnailHandlers() {
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery?.elements?.thumbnails) return;
    
    const thumbnails = mediaGallery.elements.thumbnails.querySelectorAll('[data-target]');
    
    thumbnails.forEach((thumbnail) => {
      const button = thumbnail.querySelector('button');
      if (button) {
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
        newButton.addEventListener('click', 
          mediaGallery.setActiveMedia.bind(mediaGallery, thumbnail.dataset.target, false, true)
        );
      }
    });
  }

  enableAddToCartButton() {
    const submitButton = document.querySelector('button[name="add"], .product-form__submit');
    if (submitButton && submitButton.hasAttribute('disabled')) {
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
      
      const productForm = document.querySelector('product-form');
      if (productForm && typeof productForm.toggleSubmitButton === 'function') {
        productForm.toggleSubmitButton(false);
      }
    }
  }
}

// Initialize variant gallery
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new VariantGallery());
} else {
  new VariantGallery();
}
